/**
 * Purchase Order React Query Hooks
 *
 * Provides data fetching and mutation hooks for purchase orders
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type {
  PurchaseOrder,
  CreatePurchaseOrderDto,
  UpdatePurchaseOrderDto,
  ReceivePurchaseOrderDto,
  PurchaseOrderKpis,
  PurchaseOrderStatus,
  OrderLineItem,
} from '@lognai/plugin-orders';
import {
  listPurchaseOrders,
  getPurchaseOrder,
  createPurchaseOrder,
  updatePurchaseOrder,
  updatePurchaseOrderStatus,
  cancelPurchaseOrder,
  receivePurchaseOrder,
  getPurchaseOrderLines,
  getPurchaseOrderKpis,
  type PurchaseOrderFilterParams,
  type PaginatedPurchaseOrderList,
} from '../api/purchase-orders';
import { purchaseOrderKeys } from './query-keys';

/**
 * Hook to fetch paginated purchase orders
 */
export const usePurchaseOrders = (
  params?: PurchaseOrderFilterParams,
  options?: Omit<UseQueryOptions<PaginatedPurchaseOrderList>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: purchaseOrderKeys.list(params),
    queryFn: () => listPurchaseOrders(params),
    ...options,
  });
};

/**
 * Hook to fetch a single purchase order
 */
export const usePurchaseOrder = (
  id: string,
  options?: Omit<UseQueryOptions<PurchaseOrder>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: purchaseOrderKeys.detail(id),
    queryFn: () => getPurchaseOrder(id),
    enabled: !!id,
    ...options,
  });
};

/**
 * Hook to fetch purchase order line items
 */
export const usePurchaseOrderLines = (
  id: string,
  options?: Omit<UseQueryOptions<OrderLineItem[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: purchaseOrderKeys.lines(id),
    queryFn: () => getPurchaseOrderLines(id),
    enabled: !!id,
    ...options,
  });
};

/**
 * Hook to fetch purchase order KPIs
 */
export const usePurchaseOrderKpis = (
  options?: Omit<UseQueryOptions<PurchaseOrderKpis>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: purchaseOrderKeys.kpis(),
    queryFn: getPurchaseOrderKpis,
    ...options,
  });
};

/**
 * Hook to create a purchase order
 */
export const useCreatePurchaseOrder = (
  options?: Omit<UseMutationOptions<PurchaseOrder, Error, CreatePurchaseOrderDto>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPurchaseOrder,
    ...options,
    // Spread first, then compose: a page's `onSuccess` (usually navigate) must
    // run AFTER the invalidation, not replace it — otherwise the detail page
    // shows stale data for the whole staleTime after an edit.
    onSuccess: (data, variables, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.kpis() });
      options?.onSuccess?.(data, variables, context, mutation);
    },
  });
};

/**
 * Hook to update a purchase order
 */
export const useUpdatePurchaseOrder = (
  options?: Omit<
    UseMutationOptions<PurchaseOrder, Error, { id: string; dto: UpdatePurchaseOrderDto }>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }) => updatePurchaseOrder(id, dto),
    ...options,
    onSuccess: (data, variables, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(data.id) });
      // Refresh line items now that they're editable.
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.lines(data.id) });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.kpis() });
      options?.onSuccess?.(data, variables, context, mutation);
    },
  });
};

/**
 * Hook to update purchase order status
 */
export const useUpdatePurchaseOrderStatus = (
  options?: Omit<
    UseMutationOptions<
      PurchaseOrder,
      Error,
      { id: string; status: PurchaseOrderStatus; reason?: string }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, reason }) => updatePurchaseOrderStatus(id, status, reason),
    ...options,
    onSuccess: (data, variables, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.kpis() });
      options?.onSuccess?.(data, variables, context, mutation);
    },
  });
};

/**
 * Hook to cancel a purchase order
 */
export const useCancelPurchaseOrder = (
  options?: Omit<
    UseMutationOptions<PurchaseOrder, Error, { id: string; reason?: string }>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }) => cancelPurchaseOrder(id, reason),
    ...options,
    onSuccess: (data, variables, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.kpis() });
      options?.onSuccess?.(data, variables, context, mutation);
    },
  });
};

/**
 * Hook to receive goods for a purchase order
 */
export const useReceivePurchaseOrder = (
  options?: Omit<
    UseMutationOptions<PurchaseOrder, Error, { id: string; dto: ReceivePurchaseOrderDto }>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }) => receivePurchaseOrder(id, dto),
    ...options,
    onSuccess: (data, variables, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.lines(data.id) });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.kpis() });
      options?.onSuccess?.(data, variables, context, mutation);
    },
  });
};
